import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Transition } from 'react-transition-group'
import PropTypes from 'prop-types'
import { CloseIcon, Content, GlobalStyles, Root } from './Dialog.styles'

const TRANSITION_DURATION = 200
const defaultStyle = {
  opacity: 0,
  transition: `opacity ${TRANSITION_DURATION}ms ease-in-out`
}
const transitionStyles = {
  entered: { opacity: 1 },
  exited: { opacity: 0 }
}

/**
 * The Dialog' component.
 */
const Dialog = props => {
  const {
    children,
    disableBackdropClick,
    disableBackdropColor,
    disableCloseIcon,
    margin,
    maxWidth,
    open,
    padding,
    position,
    verticalAlign,
    onClose,
    onOpen,
    ...restProps
  } = props
  const rootRef = useRef(undefined)
  const handleBackdropClick = () => (disableBackdropClick ? () => undefined : onClose())
  const handleClick = useCallback(event => event.stopPropagation(), [])
  const handleEscape = useCallback(
    event => {
      if (event.key !== 'Escape' || event.keyCode !== 27) {
        return
      }

      onClose(event)
    },
    [onClose]
  )
  const portalElement = useMemo(() => {
    let element = document.getElementById('__portal')

    if (!element) {
      element = document.createElement('div')

      element.setAttribute('id', '__portal')
      document.body.appendChild(element)
    }

    element.style.height = 'auto'
    element.style.position = 'fixed'
    element.style.top = '0'
    element.style.width = '100%'
    element.style.zIndex = '1300'

    return element
  }, [])

  useEffect(() => {
    if (!open || !rootRef.current) {
      return
    }

    if (open) {
      onOpen()
    }

    rootRef.current.focus()
  }, [open, onOpen])

  if (!portalElement) {
    return null
  }

  return createPortal(
    <>
      <GlobalStyles show={open ? 1 : 0} />
      <Transition mountOnEnter unmountOnExit in={open} timeout={TRANSITION_DURATION}>
        {state => (
          <Root
            {...restProps}
            disableBackdropColor={disableBackdropColor}
            ref={rootRef}
            show={open}
            style={{ ...defaultStyle, ...transitionStyles[state] }}
            tabIndex="-1"
            verticalAlign={verticalAlign}
            onClick={handleBackdropClick}
            onKeyDown={handleEscape}
          >
            <Content margin={margin} maxWidth={maxWidth} padding={padding} position={position} onClick={handleClick}>
              {!disableCloseIcon && <CloseIcon onClick={onClose} />}
              {children}
            </Content>
          </Root>
        )}
      </Transition>
    </>,
    portalElement
  )
}

Dialog.defaultProps = {
  disableBackdropClick: false,
  disableBackdropColor: false,
  disableCloseIcon: false,
  margin: '100px auto',
  maxWidth: '500px',
  padding: '20px',
  verticalAlign: 'center',
  onClose: () => undefined,
  onOpen: () => undefined
}

Dialog.propTypes = {
  /**
   * Children.
   */
  children: PropTypes.node.isRequired,
  /**
   * Disable backdrop click.
   */
  disableBackdropClick: PropTypes.bool,
  /**
   * Disable backdrop color.
   */
  disableBackdropColor: PropTypes.bool,
  /**
   * Disable close icon.
   */
  disableCloseIcon: PropTypes.bool,
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Max width.
   */
  maxWidth: PropTypes.string,
  /**
   * Open.
   */
  open: PropTypes.bool.isRequired,
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Position.
   */
  position: PropTypes.string,
  /**
   * Vertical align.
   */
  verticalAlign: PropTypes.oneOf(['bottom', 'center', 'top']),
  /**
   * On close.
   */
  onClose: PropTypes.func,
  /**
   * On open.
   */
  onOpen: PropTypes.func
}

export default Dialog
